import type { InterfaceAddVehicleForm } from '@interfaces/InterfaceAddVehicleForm.ts';
import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
import type { InterfaceGenericTypeSlug } from '@interfaces/InterfaceGenericTypeSlug.ts';
import type { InterfaceVehicle } from '@interfaces/InterfaceVehicle.ts';
import type { InterfaceVehicleFleet } from '@interfaces/InterfaceVehicleFleet.ts';
import type { InterfaceVehicleModel } from '@interfaces/InterfaceVehicleModel.ts';
import type { InterfaceVehicleSizeType } from '@interfaces/InterfaceVehicleSizeType.ts';
import { createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { vehicleSizeImageUrls } from '@store/reducers/data/vehicleSizeImageUrls.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import createDebouncedAsyncThunk from '@store/reducers/reducerHelpers/createDebouncedAsyncThunk.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import thunkDelete from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { defaultTypeDebounceTimeMs } from '@store/reducers/reducerHelpers/thunkHelperShared.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';
import { getOptionsArrayFromUuidOrSlug, type InterfaceOption } from '@utils/OptionsArrayHelpers.ts';
import { createSelector } from 'reselect';

export interface VehicleReducerState {
  vehicleMakeTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  vehicleModels: InterfaceGenericMap<InterfaceVehicleModel>;
  vehicleSizeTypes: InterfaceGenericMap<InterfaceVehicleSizeType>;
  vehicleStatusTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  vehicleFeatureTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  vehicleColourTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  vehicleStatusTypeColourMap: {
    [vehicleStatusTypeSlug: string]: string;
  };
  vehicleTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  vehicles: InterfaceGenericMap<InterfaceVehicle>;
  fleets: InterfaceGenericMap<InterfaceVehicleFleet>;
  vehicleSizeImageUrls: InterfaceGenericMap<string>;
  updatingVehicleStatuses: string[]; // Track which vehicles are having their status updated
}

const initialState: VehicleReducerState = {
  vehicleMakeTypes: {},
  vehicleModels: {},
  vehicleSizeTypes: {},
  vehicleStatusTypes: {},
  vehicleColourTypes: {},
  vehicleFeatureTypes: {},
  vehicleStatusTypeColourMap: {
    available: 'success',
    booked: 'cb_orange',
    maintenance: 'cb_red',
    offline: 'cb_red',
    'on-delivery': 'cb_lightblue',
    unavailable: 'cb_red',
  },
  vehicleTypes: {},
  vehicles: {},
  fleets: {},
  vehicleSizeImageUrls,
  updatingVehicleStatuses: [] as string[],
};

const sliceName = 'vehicle';

export const vehicleSlice = createSlice({
  name: sliceName, // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      // Handle updateVehicleStatus loading states
      .addCase(updateVehicleStatus.pending, (state, action) => {
        const vehicleUuid = action.meta.arg?.form?.vehicleUuid;
        if (vehicleUuid && !state.updatingVehicleStatuses.includes(vehicleUuid)) {
          state.updatingVehicleStatuses.push(vehicleUuid);
        }
      })
      .addCase(updateVehicleStatus.fulfilled, (state, action) => {
        const vehicleUuid = action.meta.arg?.form?.vehicleUuid;
        if (vehicleUuid) {
          state.updatingVehicleStatuses = state.updatingVehicleStatuses.filter((uuid) => uuid !== vehicleUuid);
        }
        sliceStateCheckAndUpdate.sliceItemElementUuid('vehicles')(state, action);
      })
      .addCase(updateVehicleStatus.rejected, (state, action) => {
        const vehicleUuid = action.meta.arg?.form?.vehicleUuid;
        if (vehicleUuid) {
          state.updatingVehicleStatuses = state.updatingVehicleStatuses.filter((uuid) => uuid !== vehicleUuid);
        }
      })
      .addCase(getVehicleTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleTypes'))
      .addCase(getVehicleSizeTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleSizeTypes'))
      .addCase(getVehicleStatusTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleStatusTypes'))
      .addCase(getVehicleColourTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleColourTypes'))
      .addCase(getVehicleFeatureTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleFeatureTypes'))
      .addCase(getVehicleMakeTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleMakeTypes'))
      .addCase(getVehicleModels.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicleModels'))
      .addCase(getCompanyVehicles.fulfilled, sliceStateCheckAndUpdate.sliceItem('vehicles'))
      .addCase(getFleets.fulfilled, sliceStateCheckAndUpdate.sliceItem('fleets'))
      .addCase(addCompanyVehicle.fulfilled, sliceStateCheckAndUpdate.sliceItemElementUuid('vehicles'))
      .addCase(updateCompanyVehicle.fulfilled, sliceStateCheckAndUpdate.sliceItemElementUuid('vehicles'))
      // Handle vehicle deletion
      .addCase(delVehicle.fulfilled, (state, action) => {
        const vehicleUuid = action.meta.arg?.form?.vehicleUuid;
        if (vehicleUuid && state.vehicles[vehicleUuid]) {
          delete state.vehicles[vehicleUuid];
        }
      });
  },
});

export const getVehicleTypes = thunkGet.get(sliceName, `type/vehicle`);
export const getVehicleStatusTypes = thunkGet.get(sliceName, `type/vehicle/status`);
export const getVehicleSizeTypes = thunkGet.get(sliceName, `type/vehicle/size`);
export const getVehicleColourTypes = thunkGet.get(sliceName, `type/vehicle/colour`);
export const getVehicleFeatureTypes = thunkGet.get(sliceName, `type/vehicle/feature`);
export const getVehicleMakeTypes = thunkGet.get(sliceName, `type/vehicle/make`);
export const getVehicleModels = thunkGet.get(sliceName, `type/vehicle/model`);
export const getCompanyVehicles = thunkGet.compUrl(sliceName, `c/{companySlug}/admin/vehicle`);

export const getFleets = thunkGet.get(sliceName, 'c/{companySlug}/admin/fleet');

export const addCompanyVehicle = thunkPost.compUrl<InterfaceAddVehicleForm>(
  'vehicle',
  '/c/{companySlug}/admin/vehicle',
);

export interface InterfaceDelVehicleForm {
  vehicleUuid: string;
}
export const delVehicle = thunkDelete.compUrl<InterfaceDelVehicleForm, { '{vehicleUuid}': string }>(
  'vehicle',
  '/c/{companySlug}/admin/vehicle/{vehicleUuid}',
);

export interface InterfaceUpdateVehicleStatusForm {
  vehicleUuid: string;
  vehicleStatusTypeSlug: string;
}
export const updateVehicleStatus = thunkPost.compUrl<InterfaceUpdateVehicleStatusForm>(
  'vehicle',
  '/c/{companySlug}/admin/vehicle/status',
  [getCompanyVehicles()], // Get updated list of vehicles after change
);

// ============= NEW CRUD OPERATIONS =============

// Update Vehicle Interface (for full updates using PUT)
export interface InterfaceUpdateVehicleForm {
  vehicleMakeTypeSlug?: string;
  vehicleModelUuid?: string;
  vehicleTypeSlug?: string;
  vehicleSizeTypeSlug?: string;
  vehicleColourTypeSlug?: string;
  vehicleFeatureTypeSlugs?: string[];
  vehicleStatusTypeSlug?: string;
  registrationNumber?: string;
  year?: number;
  notes?: string;
  fleetUuid?: string;
}

// Update Vehicle (Full Update)
export const updateCompanyVehicle = thunkPut.compUrl<InterfaceUpdateVehicleForm, { '{vehicleUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/vehicle/{vehicleUuid}',
  [getCompanyVehicles()],
);

export const selectVehicleState = (state: AppState) => state.vehicle;
export const selectVehicleStatusTypes = (state: AppState) => selectVehicleState(state).vehicleStatusTypes;
export const selectVehicleModels = (state: AppState) => selectVehicleState(state).vehicleModels;
export const selectVehicleSizeTypes = (state: AppState) => selectVehicleState(state).vehicleSizeTypes;
export const selectVehicleStatusTypeColourMap = (state: AppState) =>
  selectVehicleState(state).vehicleStatusTypeColourMap;
export const selectVehicleMakeTypes = (state: AppState) => selectVehicleState(state).vehicleMakeTypes;
export const selectVehicleMakeTypesOptions = (state: AppState): InterfaceOption[] =>
  getOptionsArrayFromUuidOrSlug(selectVehicleMakeTypes(state));
export const selectVehicleTypes = (state: AppState) => selectVehicleState(state).vehicleTypes;
export const selectVehicleTypesOptions = (state: AppState): InterfaceOption[] =>
  getOptionsArrayFromUuidOrSlug(selectVehicleTypes(state));

// Replace lodash memoize with Redux createSelector
export const selectVehicles = createSelector([selectVehicleState], (vehicleState) => vehicleState.vehicles);

export const selectVehicleFeatureTypes = (state: AppState) => selectVehicleState(state).vehicleFeatureTypes;
export const selectVehicleColourTypes = (state: AppState) => selectVehicleState(state).vehicleColourTypes;
export const selectVehicleColorTypeOptions = (state: AppState): InterfaceOption[] =>
  getOptionsArrayFromUuidOrSlug(selectVehicleColourTypes(state));

// Replace lodash memoize with Redux createSelector
export const selectVehicleArray = createSelector([selectVehicles], (vehicles): InterfaceVehicle[] =>
  Object.values(vehicles),
);

export const selectVehicleFleets = (state: AppState) => selectVehicleState(state).fleets;
export const selectVehicleSizeImageUrls = (state: AppState) => selectVehicleState(state).vehicleSizeImageUrls;
export const selectVehicleSizeImage = (vehicleSizeTypeSlug: string) => (state: AppState) =>
  selectVehicleSizeImageUrls(state)[vehicleSizeTypeSlug] ?? selectVehicleSizeImageUrls(state).unknown;
export const selectUpdatingVehicleStatuses = (state: AppState) =>
  selectVehicleState(state).updatingVehicleStatuses || [];
export const selectIsVehicleStatusUpdating = (vehicleUuid: string) => (state: AppState) =>
  selectUpdatingVehicleStatuses(state).includes(vehicleUuid);

// export const { } = vehicleSlice.actions;

export default vehicleSlice.reducer;

// Only for initial load of data that doesn't require authentication
// This is only done to speed things up. It is not required.
// This data should be reloaded in the pages that need it.
// Load any data a page needs in the page. Else it will not update ever.
export const loadInitialVehicleData = createDebouncedAsyncThunk(
  `${sliceName}/initial`,
  async (_, { dispatch }) => {
    dispatch(getVehicleTypes());
    dispatch(getVehicleStatusTypes());
    dispatch(getVehicleColourTypes());
    dispatch(getVehicleFeatureTypes());
    dispatch(getVehicleMakeTypes());
  },
  defaultTypeDebounceTimeMs,
);
